(() => {
  if (customElements.get('atlasgrey-flexible-buybox')) return;

  class AtlasgreyFlexibleBuybox extends HTMLElement {
    constructor() {
      super();
      this.productData = null;
      this.slingData = null;
      this.buyboxThumbnailMap = {};
      this.selectedOption = null;
      this.currentVariantId = Number(this.dataset.currentVariantId || 0);
      this.basePrice = Number(this.dataset.basePrice || 0);
      this.comparePrice = Number(this.dataset.comparePrice || 0);
      this.variantSelections = {};
      this.priceState = new Map();
      this.preorderTravelly = null;
      this.preorderSling = null;
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.options = [...this.querySelectorAll('[data-offer]')];
      this.button = this.querySelector('[data-add-to-cart]');
      this.buttonLabel = this.querySelector('[data-button-label]');
      this.buttonSubtext = this.querySelector('[data-button-subtext]');
      this.buttonPrice = this.querySelector('[data-button-price]');
      this.buttonCompare = this.querySelector('[data-button-compare]');
      this.buttonSeparator = this.querySelector('[data-button-separator]');
      this.errorElement = this.querySelector('[data-error]');
      this.buyboxThumbnailMap = this.readBuyboxThumbnailMap();

      this.setupEventListeners();
      this.selectInitialOption();
      this.loadData();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:update', this.onVariantUpdate);
    }

    async loadData() {
      const productUrl = this.dataset.productJsonUrl;
      const slingHandle = this.dataset.slingHandle;
      const root = this.dataset.rootUrl || '/';
      const slingUrl = slingHandle
        ? `${root.replace(/\/?$/, '/') }products/${encodeURIComponent(slingHandle)}.js`
        : null;

      try {
        const [productResponse, slingResponse] = await Promise.all([
          fetch(productUrl),
          slingUrl ? fetch(slingUrl) : Promise.resolve(null)
        ]);

        if (!productResponse.ok) throw new Error('Produktdaten konnten nicht geladen werden.');
        this.productData = await productResponse.json();

        if (slingResponse && slingResponse.ok) {
          this.slingData = await slingResponse.json();
        }

        const liveVariant = this.productData.variants.find(
          (variant) => Number(variant.id) === this.currentVariantId
        );
        if (liveVariant) {
          this.basePrice = Number(liveVariant.price || this.basePrice);
          this.comparePrice = Number(liveVariant.compare_at_price || 0);
        }

        this.renderActiveSelector();
        this.updatePrices();
        this.updateButtonState();
      } catch (error) {
        console.error('[Atlasgrey Buybox]', error);
        this.showError(this.dataset.loadErrorText || 'Die Produktauswahl konnte nicht geladen werden.');
        if (this.button) this.button.disabled = true;
      }
    }

    setupEventListeners() {
      this.options.forEach((option) => {
        option.addEventListener('click', (event) => {
          if (event.target.closest('[data-selector]')) return;
          this.selectOption(option);
        });

        option.addEventListener('keydown', (event) => {
          if (event.target !== option) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.selectOption(option);
            return;
          }

          if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) {
            event.preventDefault();
            const direction = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
            const currentIndex = this.options.indexOf(option);
            const nextIndex = (currentIndex + direction + this.options.length) % this.options.length;
            const nextOption = this.options[nextIndex];
            this.selectOption(nextOption);
            nextOption.focus();
          }
        });
      });

      this.button?.addEventListener('click', () => this.addToCart());

      this.onVariantUpdate = (event) => {
        const variant = event.detail?.variant;
        if (!variant) return;

        this.currentVariantId = Number(variant.id);
        this.basePrice = Number(variant.price || 0);
        this.comparePrice = Number(variant.compare_at_price || 0);
        this.dataset.currentVariantId = String(this.currentVariantId);
        this.updatePrices();
        this.updateButtonState();
      };

      document.addEventListener('variant:update', this.onVariantUpdate);
    }

    selectInitialOption() {
      const defaults = this.options.filter((option) => option.dataset.default === 'true');
      const initial = defaults[0] || this.options[0] || null;
      if (!initial) return;

      this.options.forEach((option) => {
        const active = option === initial;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-checked', active ? 'true' : 'false');
        option.tabIndex = active ? 0 : -1;
      });
      this.selectedOption = initial;
    }

    selectOption(option) {
      if (!option || option === this.selectedOption) return;

      this.options.forEach((item) => {
        const active = item === option;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-checked', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });
      this.selectedOption = option;
      this.renderActiveSelector();
      this.updatePrices();
      this.updateButtonState();
    }

    renderActiveSelector() {
      if (!this.selectedOption || !this.productData) return;

      if (this.selectedOption.dataset.preorder === 'true') {
        this.renderPreorderSelector(this.selectedOption);
        return;
      }

      const quantity = Number(this.selectedOption.dataset.quantity || 1);
      if (quantity > 0) {
        this.renderBundleSelector(this.selectedOption, quantity);
      }
    }

    renderBundleSelector(option, quantity) {
      const container = option.querySelector('[data-selector]');
      if (!container) return;

      const optionIndex = option.dataset.optionIndex;
      const initialVariant =
        this.productData.variants.find((variant) => Number(variant.id) === this.currentVariantId) ||
        this.productData.variants.find((variant) => variant.available) ||
        this.productData.variants[0];

      if (!Array.isArray(this.variantSelections[optionIndex])) {
        this.variantSelections[optionIndex] = Array.from({ length: quantity }, () => initialVariant);
      } else {
        this.variantSelections[optionIndex].length = quantity;
        for (let index = 0; index < quantity; index += 1) {
          if (!this.variantSelections[optionIndex][index]) {
            this.variantSelections[optionIndex][index] = initialVariant;
          }
        }
      }

      container.innerHTML = '';

      const colorHeading = (this.dataset.colorHeading || '').trim();
      if (colorHeading) {
        const heading = document.createElement('div');
        heading.className = 'agbb__selector-heading';
        heading.textContent = colorHeading;
        container.appendChild(heading);
      }

      for (let index = 0; index < quantity; index += 1) {
        const row = document.createElement('div');
        row.className = 'agbb__variant-row';

        const number = document.createElement('span');
        number.className = 'agbb__row-number';
        number.textContent = `${index + 1}.`;
        number.setAttribute('aria-hidden', 'true');

        const rail = this.createVariantRail({
          variants: this.productData.variants,
          selectedId: this.variantSelections[optionIndex][index]?.id,
          accessibleLabel: `${this.dataset.itemLabel || 'Produkt'} ${index + 1}`,
          allowPreorderIds: this.getPreorderColors().map((entry) => entry.id),
          imageFallback: this.productData.featured_image,
          onSelect: (variant) => {
            this.variantSelections[optionIndex][index] = variant;
            this.updateButtonState();
          }
        });

        row.append(number, rail);
        container.appendChild(row);
      }
    }

    renderPreorderSelector(option) {
      const container = option.querySelector('[data-selector]');
      if (!container) return;

      const preorderColors = this.getPreorderColors();
      const travellyVariants = preorderColors
        .map((entry) => {
          const variant = this.productData.variants.find(
            (candidate) => Number(candidate.id) === Number(entry.id)
          );
          return variant ? { ...variant, displayName: entry.name || this.getColorName(variant.name) } : null;
        })
        .filter(Boolean);

      if (!this.preorderTravelly || !travellyVariants.some(
        (variant) => Number(variant.id) === Number(this.preorderTravelly.id)
      )) {
        this.preorderTravelly = travellyVariants[0] || null;
      }

      const slingConfigured = [
        {
          id: Number(this.dataset.preorderSlingBlack || 0),
          key: 'black',
          displayName: this.dataset.slingBlackName || 'Schwarz'
        },
        {
          id: Number(this.dataset.preorderSlingCream || 0),
          key: 'cream',
          displayName: this.dataset.slingCreamName || 'Cream'
        },
        {
          id: Number(this.dataset.preorderSlingArmy || 0),
          key: 'army',
          displayName: this.dataset.slingArmyName || 'Army Green'
        }
      ].filter((entry) => entry.id);

      const slingVariants = slingConfigured.map((entry) => {
        const variant = this.slingData?.variants?.find(
          (candidate) => Number(candidate.id) === Number(entry.id)
        );
        return variant
          ? { ...variant, displayName: entry.displayName, key: entry.key }
          : { ...entry, available: false };
      });

      const defaultSlingKey = this.dataset.preorderSlingDefault || 'black';
      const selectedSlingIsAvailable = this.preorderSling && slingVariants.some(
        (variant) =>
          Number(variant.id) === Number(this.preorderSling.id) &&
          variant.available !== false
      );
      if (!selectedSlingIsAvailable) {
        this.preorderSling =
          slingVariants.find(
            (variant) => variant.key === defaultSlingKey && variant.available !== false
          ) ||
          slingVariants.find((variant) => variant.available !== false) ||
          null;
      }

      container.innerHTML = '';

      if (travellyVariants.length) {
        container.appendChild(this.createSelectorGroup({
          title: this.dataset.preorderProductLabel || 'Travelly Pro',
          variants: travellyVariants,
          selectedId: this.preorderTravelly?.id,
          accessibleLabel: this.dataset.preorderProductLabel || 'Travelly Pro',
          allowAll: true,
          imageFallback: this.productData.featured_image,
          onSelect: (variant) => {
            this.preorderTravelly = variant;
          }
        }));
      }

      if (slingVariants.length) {
        container.appendChild(this.createSelectorGroup({
          title: this.dataset.preorderGiftLabel || 'Atlas Sling Bag (gratis)',
          variants: slingVariants,
          selectedId: this.preorderSling?.id,
          accessibleLabel: this.dataset.preorderGiftLabel || 'Atlas Sling Bag',
          allowAll: false,
          imageFallback: this.slingData?.featured_image,
          onSelect: (variant) => {
            this.preorderSling = variant;
            this.updateButtonState();
          }
        }));
      }
    }

    readBuyboxThumbnailMap() {
      const mapElement = this.querySelector('[data-buybox-thumbnail-map]');
      if (!mapElement) return {};

      try {
        const parsed = JSON.parse(mapElement.textContent || '{}');
        return {
          ...(parsed.product || {}),
          ...(parsed.sling || {})
        };
      } catch (error) {
        console.warn('[Atlasgrey Buybox] Buybox-Bildzuordnung konnte nicht gelesen werden.', error);
        return {};
      }
    }

    getVariantImage(variant, fallback = null) {
      return (
        this.buyboxThumbnailMap[String(variant.id)] ||
        variant.featured_image?.src ||
        fallback ||
        ''
      );
    }

    createSelectorGroup(config) {
      const group = document.createElement('div');
      group.className = 'agbb__group';

      const title = document.createElement('div');
      title.className = 'agbb__group-title';
      title.textContent = config.title;

      const row = document.createElement('div');
      row.className = 'agbb__variant-row';

      const number = document.createElement('span');
      number.className = 'agbb__row-number';
      number.textContent = '•';
      number.setAttribute('aria-hidden', 'true');

      row.append(number, this.createVariantRail(config));
      group.append(title, row);
      return group;
    }

    createVariantRail({
      variants,
      selectedId,
      accessibleLabel,
      allowPreorderIds = [],
      allowAll = false,
      imageFallback = null,
      onSelect
    }) {
      const rail = document.createElement('div');
      rail.className = 'agbb__tile-rail';
      rail.dataset.selector = '';
      rail.setAttribute('role', 'radiogroup');
      rail.setAttribute('aria-label', accessibleLabel);

      variants.forEach((variant) => {
        const id = Number(variant.id);
        const isSelected = id === Number(selectedId);
        const selectable = allowAll || variant.available !== false || allowPreorderIds.includes(id);
        const colorName = variant.displayName || this.getColorName(variant.name || variant.title || '');

        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'agbb__tile';
        tile.classList.toggle('is-selected', isSelected);
        tile.disabled = !selectable;
        tile.setAttribute('role', 'radio');
        tile.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        tile.setAttribute(
          'aria-label',
          `${colorName}${selectable ? '' : ` – ${this.dataset.soldOutText || 'ausverkauft'}`}`
        );

        const imageWrap = document.createElement('span');
        imageWrap.className = 'agbb__tile-image-wrap';

        const image = document.createElement('img');
        image.className = 'agbb__tile-image';
        image.loading = 'lazy';
        image.alt = `${accessibleLabel}: ${colorName}`;
        image.src = this.getVariantImage(variant, imageFallback);
        if (!image.src) image.hidden = true;

        const check = document.createElement('span');
        check.className = 'agbb__tile-check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';

        const colorBadge = document.createElement('span');
        colorBadge.className = 'agbb__tile-color-badge';
        colorBadge.textContent = colorName;
        colorBadge.setAttribute('aria-hidden', 'true');

        const name = document.createElement('span');
        name.className = 'agbb__tile-name';
        name.textContent = colorName;

        imageWrap.append(image, check);
        tile.append(imageWrap, colorBadge, name);

        tile.addEventListener('click', (event) => {
          event.stopPropagation();
          if (!selectable) return;

          rail.querySelectorAll('.agbb__tile').forEach((candidate) => {
            candidate.classList.remove('is-selected');
            candidate.setAttribute('aria-checked', 'false');
          });
          tile.classList.add('is-selected');
          tile.setAttribute('aria-checked', 'true');
          onSelect(variant);
        });

        rail.appendChild(tile);
      });

      return rail;
    }

    getPreorderColors() {
      return (this.dataset.preorderColors || '')
        .split('|')
        .filter(Boolean)
        .map((entry) => {
          const [id, ...name] = entry.split('~');
          return { id: Number(id), name: name.join('~').trim() };
        })
        .filter((entry) => entry.id);
    }

    getColorName(variantName) {
      const parts = String(variantName || '')
        .split(/[-–—/]/)
        .map((part) => part.trim())
        .filter(Boolean);
      return parts[parts.length - 1] || String(variantName || '');
    }

    calculateOption(option) {
      const quantity = Number(option.dataset.quantity || 1);
      const discount = Number(option.dataset.discount || 0);
      const isPreorder = option.dataset.preorder === 'true';
      const unitPrice = this.basePrice;
      const total = isPreorder
        ? unitPrice
        : Math.round(unitPrice * quantity * (1 - discount / 100));
      const compareBase = this.comparePrice > 0 ? this.comparePrice : this.basePrice;
      const compare = Math.round(compareBase * quantity);
      const showCompare = option.dataset.showCompare === 'true' && compare > total;
      const savings = Math.max(0, compare - total);

      return { total, compare, showCompare, savings };
    }

    updatePrices() {
      if (!this.basePrice) return;

      this.options.forEach((option) => {
        const state = this.calculateOption(option);
        this.priceState.set(option, state);

        const price = option.querySelector('[data-total-price]');
        const compare = option.querySelector('[data-original-price]');
        const savings = option.querySelector('[data-savings]');

        if (price) price.textContent = this.formatPrice(state.total);
        if (compare) {
          compare.textContent = this.formatPrice(state.compare);
          compare.hidden = !state.showCompare;
        }
        if (savings) {
          savings.textContent = `${this.dataset.savingsPrefix || 'Rabatt:'} ${this.formatPrice(state.savings)}`;
          savings.hidden = state.savings <= 0;
        }
      });

      this.updateButtonPrices();
    }

    updateButtonPrices() {
      if (!this.selectedOption || !this.buttonPrice) return;
      const state = this.priceState.get(this.selectedOption) || this.calculateOption(this.selectedOption);
      this.buttonPrice.textContent = this.formatPrice(state.total);

      if (this.buttonCompare) {
        this.buttonCompare.textContent = this.formatPrice(state.compare);
        this.buttonCompare.hidden = !state.showCompare;
      }
      if (this.buttonSeparator) {
        this.buttonSeparator.hidden = false;
      }
    }

    formatPrice(cents) {
      const number = Number(cents || 0) / 100;
      const decimals = number.toFixed(2).replace('.', ',');
      return `${this.dataset.currencySymbol || '€'}${decimals}`;
    }

    getSelectedVariants() {
      if (!this.selectedOption || !this.productData) return [];
      if (this.selectedOption.dataset.preorder === 'true') return [];

      const quantity = Number(this.selectedOption.dataset.quantity || 1);
      return (this.variantSelections[this.selectedOption.dataset.optionIndex] || []).filter(Boolean);
    }

    hasPreorderVariantSelected() {
      const preorderBlocked =
        this.selectedOption.dataset.preorder === 'true' &&
        (
          !this.preorderTravelly?.id ||
          !this.preorderSling?.id ||
          this.preorderSling.available === false
        );
      if (preorderBlocked) {
        this.showError(this.dataset.unavailableErrorText || 'Diese Farbe ist leider ausverkauft.');
        return;
      }

      const preorderIds = this.getPreorderColors().map((entry) => Number(entry.id));
      return this.getSelectedVariants().some(
        (variant) => variant && preorderIds.includes(Number(variant.id))
      );
    }

    updateButtonState() {
      if (!this.button || this.isSubmitting) return;

      this.button.classList.remove('is-sold-out');
      this.button.disabled = false;
      this.buttonSubtext.hidden = true;

      if (this.selectedOption?.dataset.preorder === 'true') {
        this.buttonLabel.textContent = this.dataset.preorderButtonText || 'Jetzt vorbestellen';
      } else if (this.hasPreorderVariantSelected()) {
        this.buttonLabel.textContent = this.dataset.preorderButtonText || 'Jetzt vorbestellen';
        const subtext = this.dataset.preorderButtonSubtext;
        if (subtext) {
          this.buttonSubtext.textContent = subtext;
          this.buttonSubtext.hidden = false;
        }
      } else {
        this.buttonLabel.textContent = this.dataset.buttonText || 'In den Warenkorb';
      }

      if (this.selectedOption?.dataset.preorder === 'true') {
        const preorderUnavailable =
          !this.preorderTravelly?.id ||
          !this.preorderSling?.id ||
          this.preorderSling.available === false;
        if (preorderUnavailable) {
          this.buttonLabel.textContent = this.dataset.soldOutText || 'Ausverkauft';
          this.buttonSubtext.hidden = true;
          this.button.disabled = true;
          this.button.classList.add('is-sold-out');
        }
      } else if (this.productData) {
        const unavailable = this.getSelectedVariants().some((variant) => variant.available === false);
        if (unavailable) {
          this.buttonLabel.textContent = this.dataset.soldOutText || 'Ausverkauft';
          this.buttonSubtext.hidden = true;
          this.button.disabled = true;
          this.button.classList.add('is-sold-out');
        }
      }

      this.updateButtonPrices();
    }

    buildCartItems() {
      if (!this.selectedOption) return [];

      if (this.selectedOption.dataset.preorder === 'true') {
        const travellyId = Number(this.preorderTravelly?.id || 0);
        const slingId = Number(this.preorderSling?.id || 0);
        return [
          travellyId ? { id: travellyId, quantity: 1 } : null,
          slingId ? { id: slingId, quantity: 1 } : null
        ].filter(Boolean);
      }

      return this.getSelectedVariants().map((variant) => ({
        id: Number(variant.id),
        quantity: 1
      }));
    }

    async addToCart() {
      if (this.isSubmitting || !this.selectedOption) return;

      const items = this.buildCartItems();
      if (!items.length) {
        this.showError(this.dataset.selectionErrorText || 'Bitte wähle ein Angebot aus.');
        return;
      }

      const preorderBlocked =
        this.selectedOption.dataset.preorder === 'true' &&
        (
          !this.preorderTravelly?.id ||
          !this.preorderSling?.id ||
          this.preorderSling.available === false
        );
      if (preorderBlocked) {
        this.showError(this.dataset.unavailableErrorText || 'Diese Farbe ist leider ausverkauft.');
        return;
      }

      const preorderIds = this.getPreorderColors().map((entry) => Number(entry.id));
      const blocked = this.getSelectedVariants().some(
        (variant) => variant.available === false && !preorderIds.includes(Number(variant.id))
      );
      if (blocked) {
        this.showError(this.dataset.unavailableErrorText || 'Diese Farbe ist leider ausverkauft.');
        return;
      }

      this.isSubmitting = true;
      this.button.disabled = true;
      this.buttonLabel.textContent = this.dataset.loadingText || 'Wird hinzugefügt …';
      this.buttonSubtext.hidden = true;

      try {
        const response = await fetch(`${this.dataset.rootUrl || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });
        const resource = await response.json();
        if (!response.ok) {
          throw new Error(resource.description || resource.message || 'Cart error');
        }

        this.buttonLabel.textContent = this.dataset.addedText || '✓ Hinzugefügt';
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource,
            sourceId: this.id || 'atlasgrey-flexible-buybox',
            data: { items }
          }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { source: 'atlasgrey-flexible-buybox' }
        }));

        window.setTimeout(() => {
          this.isSubmitting = false;
          this.updateButtonState();
        }, 1600);
      } catch (error) {
        console.error('[Atlasgrey Buybox] Cart error', error);
        this.showError(error.message || this.dataset.cartErrorText || 'Bitte versuche es erneut.');
        this.buttonLabel.textContent = this.dataset.cartErrorText || 'Erneut versuchen';
        window.setTimeout(() => {
          this.isSubmitting = false;
          this.updateButtonState();
        }, 1800);
      }
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.classList.add('is-visible');
      window.clearTimeout(this.errorTimer);
      this.errorTimer = window.setTimeout(() => {
        this.errorElement.classList.remove('is-visible');
      }, 4000);
    }
  }

  customElements.define('atlasgrey-flexible-buybox', AtlasgreyFlexibleBuybox);
})();
